import { useCallback, useState } from "react";
import { Card, CardHeader, CardContent, Typography, Box, IconButton, Popover } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { makeStyles } from "../../../../theme/theme";
import type { RootState } from "../../../../store/state/rootReducer";
import { SetSkipTime, EditIndexMetadataItem } from "../../../../store/state/story_authoring/reducer";
import type { ITime } from "../../../../store/state/story_authoring/reducer";
import type { AppDispatch } from "../../../../store/state/rootStore";
import { secondsToTimecode } from "../../TSVideoPlayer/utils";

const useStyles = makeStyles()(() => ({
  popoverButton: {
    border: "none",
    background: "none",
    color: "rgba(0, 0, 0, 0.87)",
    textAlign: "left",
    height: "38px",
    paddingLeft: "16px",
    fontSize: "14px",
    lineHeight: "24px",
    fontWeight: 400,
    "&:focus": {
      outline: "none",
    },
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
  inputNumber: {
    maxWidth: "15px",
    margin: "0px 0px 0px 20px",
    padding: "0px",
    border: "none",
    position: "absolute",
    right: 0,
    "&::-webkit-inner-spin-button": {
      opacity: 1,
      cursor: "pointer",
    },
  },
}));

interface UrlParams {
  index_id: string;
}

interface ChapterCardProps {
  item: any; // IndexItem = Bookmark | Chapter
  itemPosition: number;
  isReadOnly?: boolean;
  handleEdit: () => void;
  handleDelete: () => void;
  onCreateClip: () => void;
}

const ChapterCard = ({ item, itemPosition, isReadOnly, handleEdit, handleDelete, onCreateClip }: ChapterCardProps) => {
  const { classes } = useStyles();
  const dispatch = useDispatch<AppDispatch>();
  const { index_id: indexId } = useParams<keyof UrlParams>();
  const storyDuration = useSelector((s: RootState) => s.storyAuthoring.duration) || 0;
  const seekTo = useCallback((time) => dispatch(SetSkipTime(time)), [dispatch]);

  // item menu
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const id = menuOpen ? "popover" : undefined;

  const onEdit = (startTime: number) => {
    const metadataItem = {
      ...item,
      timecode: secondsToTimecode(startTime),
      time: {
        start: startTime,
        end: item.time.end,
      } as ITime,
    };
    dispatch(EditIndexMetadataItem({ indexId, itemPosition, item: metadataItem }));
  };

  const onTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = Number(e.target.value);
    onEdit(time);
  };

  return (
    <Card
      sx={{
        width: "100%",
        marginY: "10px",
        ":hover": { background: "rgba(136, 136, 136, 0.05)", border: "1px solid rgba(118, 118, 118, 0.6)" },
      }}
      variant="outlined"
    >
      <CardHeader
        avatar={
          <Box sx={{ display: "flex", alignItems: "center", position: "relative", padding: "5px", width: "90px" }}>
            <Typography
              variant="h5"
              sx={{ color: "#6E767E", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}
              onClick={() => seekTo(item?.time?.start || 0)}
            >
              {secondsToTimecode(item?.time?.start || 0)}
            </Typography>
            {!isReadOnly && (
              <input
                type="number"
                aria-label="set chapter time"
                min={0}
                max={storyDuration}
                value={item?.time?.start || 0}
                onChange={onTimeChange}
                className={classes.inputNumber}
              />
            )}
          </Box>
        }
        action={
          !isReadOnly && (
            <>
              <IconButton
                aria-label="item-menu"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                  "&:focus": {
                    outline: "none",
                  },
                  marginLeft: "5px",
                  height: "40px",
                }}
              >
                <MoreVertIcon />
              </IconButton>
              <Popover
                anchorEl={anchorEl}
                id={id}
                open={menuOpen}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "176px",
                  }}
                >
                  <button
                    className={classes.popoverButton}
                    onClick={() => {
                      setAnchorEl(null);
                      handleEdit();
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className={classes.popoverButton}
                    onClick={() => {
                      setAnchorEl(null);
                      handleDelete();
                    }}
                  >
                    Delete
                  </button>
                  <button
                    className={classes.popoverButton}
                    onClick={() => {
                      setAnchorEl(null);
                      onCreateClip();
                    }}
                  >
                    Create clip
                  </button>
                </Box>
              </Popover>
            </>
          )
        }
        sx={{ paddingBottom: "0px" }}
      />
      <CardContent>
        <Typography variant="h5" sx={{ color: "#323232", fontSize: "14px", fontWeight: 700 }}>
          {item.title}
        </Typography>
        {item.synopsis && (
          <Typography variant="body2" sx={{ color: "#323232", fontSize: "13px", fontWeight: 400 }}>
            {item.synopsis}
          </Typography>
        )}
        {item.keywords && (
          <Box sx={{ display: "flex", flexDirection: "column", marginTop: "16px" }}>
            <Typography variant="body2" sx={{ color: "#6F6F6F", fontSize: "12px", fontWeight: 700 }}>
              Keywords
            </Typography>
            <Typography variant="body2" sx={{ color: "#6F6F6F", fontSize: "12px", fontWeight: 700 }}>
              {item.keywords}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ChapterCard;
